import React, { memo } from "react";
import { TouchableOpacity, View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { cn } from "@/lib/utils";

export type CheckboxVariant = "default" | "success";

export interface CheckboxProps {
  /** Whether the checkbox is currently checked. */
  checked: boolean;
  /** Called when the user taps the checkbox or its label. */
  onChange: (checked: boolean) => void;
  /** Optional label text displayed beside the checkbox. */
  label?: TranslationKeys;
  /** Appends a required asterisk (*) to the label. */
  required?: boolean;
  disabled?: boolean;
  /**
   * Visual variant for the checked state.
   * - `default` — primary colour fill (blue)
   * - `success`  — green-50 fill (approval / success)
   */
  variant?: CheckboxVariant;
  /** Forwarded to the root element for testing. */
  testID?: string;
  /** Screen-reader label (translation key). Defaults to `label` when set. */
  accessibilityLabel?: TranslationKeys;
  /**
   * Custom checkmark icon rendered inside the checked box.
   * Defaults to a "✓" character via AppText.
   */
  checkIcon?: React.ReactNode;
  /** Additional className for the root element. */
  className?: string;
  labelClassName?: string;
}

// ─── Style helpers ───────────────────────────────────────────────────────────

function boxClass(checked: boolean, disabled: boolean, variant: CheckboxVariant): string {
  if (disabled) {
    return checked
      ? "size-5 rounded border-2 border-stroke-50 bg-stroke-50 items-center justify-center"
      : "size-5 rounded border-2 border-stroke-50 bg-stroke-20 items-center justify-center";
  }
  if (!checked) {
    return "size-5 rounded border-2 border-stroke-50 bg-white items-center justify-center";
  }
  return variant === "success"
    ? "size-5 rounded border-2 border-green-50 bg-green-50 items-center justify-center"
    : "size-5 rounded border-2 border-primary bg-primary items-center justify-center";
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Accessible checkbox with an optional text label.
 *
 * Matches the checkbox shown in the pre-vetting form's mandatory declaration
 * section. Three visual states:
 * - unchecked — white box with `stroke-50` border
 * - checked   — `primary` fill and border with a white checkmark
 * - disabled  — muted grey fill, non-interactive
 *
 * Pass `required` to append a red asterisk to the label.
 *
 * @example
 * const [checked, setChecked] = useState(false);
 *
 * <Checkbox
 *   checked={checked}
 *   onChange={setChecked}
 *   label="I hereby declare..."
 *   required
 * />
 */
export const Checkbox = memo(function Checkbox({
  checked,
  onChange,
  label,
  required = false,
  disabled = false,
  variant = "default",
  testID,
  accessibilityLabel,
  checkIcon,
  className,
  labelClassName,
}: CheckboxProps) {
  const { t } = useI18n();
  const a11yLabel = accessibilityLabel
    ? t(accessibilityLabel)
    : label
      ? t(label)
      : t("ui.checkbox.defaultA11y");

  function handlePress() {
    if (!disabled) {
      onChange(!checked);
    }
  }

  return (
    <View className={cn(className)}>
      <TouchableOpacity
        testID={testID}
        onPress={handlePress}
        disabled={disabled}
        activeOpacity={0.7}
        accessibilityRole="checkbox"
        accessibilityLabel={a11yLabel}
        accessibilityState={{ checked, disabled }}
        className="flex-row items-start gap-3"
      >
        {/* ── Box ──────────────────────────────────────────────────── */}
        <View className={boxClass(checked, disabled, variant)}>
          {checked &&
            (checkIcon != null ? (
              checkIcon
            ) : (
              <AppText
                weight="bold"
                className="text-white text-xs leading-none"
                accessibilityElementsHidden
                importantForAccessibility="no"
              >
                ✓
              </AppText>
            ))}
        </View>

        {/* ── Label ────────────────────────────────────────────────── */}
        {label != null && (
          <AppText
            weight="medium"
            className={cn(
              "flex-1 text-start text-base leading-snug",
              disabled ? "text-dark-text-10" : "text-supporting-text",
              labelClassName
            )}
          >
            {t(label)}
            {required && (
              <AppText
                weight="medium"
                className="text-error text-base leading-snug"
                accessible={false}
              >
                {" *"}
              </AppText>
            )}
          </AppText>
        )}
      </TouchableOpacity>
    </View>
  );
});
